import { useNavigate } from "@tanstack/react-router";
import { Command } from "cmdk";
import { ArrowRight, BookOpen, Contrast, FolderKanban, Keyboard, Moon, Palette, Plus, Search, Settings2, ShieldHalf, Sparkles, Sun } from "lucide-react";
import { Dialog as D } from "radix-ui";
import { useEffect, useState, type ReactNode } from "react";
import { askGuv, GradeBadge, Kbd } from "@/design";
import { useEnvironments, useMe, useProjects } from "@/lib/api";
import { adminElsewhere, adminUrl, appUrl, onAdminHost } from "@/lib/adminHost";
import { docsUrl } from "@/lib/config";
import { adminNav } from "./adminNav";
import { OPEN_PALETTE_EVENT, useRegisteredCommands } from "./commands";
import { useProjectSlug, useWorkspaceSlug } from "./context";
import { projectNav } from "./nav";
import { openShortcuts } from "./Shortcuts";
import { useTheme } from "./theme";

const itemClass =
  "flex h-10 cursor-default select-none items-center gap-3 rounded-md px-3 text-base text-text data-[selected=true]:bg-surface-2 [&_svg]:size-4 [&_svg]:shrink-0 [&>svg]:text-muted";

function Item({ icon, children, hint, onSelect, value, keywords }: { icon?: ReactNode; children: ReactNode; hint?: ReactNode; onSelect: () => void; value: string; keywords?: string[] }) {
  return (
    <Command.Item value={value} keywords={keywords} onSelect={onSelect} className={itemClass}>
      {icon}
      <span className="flex-1 truncate">{children}</span>
      {hint && <span className="text-xs text-subtle">{hint}</span>}
    </Command.Item>
  );
}

function Group({ heading, children }: { heading: string; children: ReactNode }) {
  return (
    <Command.Group
      heading={heading}
      className="px-2 pb-2 [&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:pb-1.5 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:text-2xs [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-[0.08em] [&_[cmdk-group-heading]]:text-subtle"
    >
      {children}
    </Command.Group>
  );
}

/** ⌘K / Ctrl+K. Jump anywhere, switch environment, run actions. Screens add their own via useRegisterCommands. */
/** `admin`: the platform admin console, which has no workspace in its URL and its own pages. */
export function CommandPalette({ admin = false }: { admin?: boolean }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const workspace = useWorkspaceSlug();
  const project = useProjectSlug();
  const projects = useProjects(workspace);
  const envs = useEnvironments(project);
  const theme = useTheme();
  const registered = useRegisteredCommands();
  const me = useMe();
  const isAdmin = me.data?.user?.is_platform_admin === true;
  const homeWorkspace = workspace || me.data?.workspaces[0]?.slug || "";

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    const onOpen = () => setOpen(true);
    window.addEventListener("keydown", onKey);
    window.addEventListener(OPEN_PALETTE_EVENT, onOpen);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener(OPEN_PALETTE_EVENT, onOpen);
    };
  }, []);

  const run = (fn: () => void) => () => {
    setOpen(false);
    fn();
  };

  return (
    <D.Root open={open} onOpenChange={setOpen}>
      <D.Portal>
        <D.Overlay className="fixed inset-0 z-50 bg-overlay data-[state=open]:animate-fade-in" />
        <D.Content className="fixed left-1/2 top-[14vh] z-50 w-[calc(100vw-2rem)] max-w-xl -translate-x-1/2 overflow-hidden rounded-xl border border-border bg-surface shadow-lg data-[state=open]:animate-pop-in" aria-describedby={undefined}>
          <D.Title className="sr-only">Command palette</D.Title>
          <Command loop label="Command palette">
            <div className="flex items-center gap-3 border-b border-border px-4">
              <Search className="size-[18px] text-subtle" />
              <Command.Input autoFocus placeholder={admin ? "Search admin pages and actions…" : "Search projects, pages and actions…"} className="h-13 flex-1 bg-transparent text-md text-text outline-none placeholder:text-subtle" />
              <Kbd>Esc</Kbd>
            </div>
            <Command.List className="scrollbar-thin max-h-[min(60vh,28rem)] overflow-y-auto py-2">
              <Command.Empty className="px-4 py-10 text-center text-base text-muted">Nothing matches. Try a project name or “deploy”.</Command.Empty>

              {project && (
                <Group heading="This project">
                  {projectNav.map((n) => (
                    <Item
                      key={n.key}
                      value={`go ${n.label}`}
                      icon={n.icon}
                      hint={<span className="flex gap-1"><Kbd>G</Kbd><Kbd>{n.key2.toUpperCase()}</Kbd></span>}
                      onSelect={run(() => navigate({ to: n.to, params: { workspace, project } as never, search: ((s: Record<string, unknown>) => s) as never }))}
                    >
                      {n.label}
                    </Item>
                  ))}
                </Group>
              )}

              {project && (envs.data?.data.length ?? 0) > 1 && (
                <Group heading="Switch environment">
                  {envs.data!.data.map((e) => (
                    <Item key={e.id} value={`env ${e.name}`} keywords={["environment", e.kind]} icon={<ArrowRight />} onSelect={run(() => navigate({ to: ".", search: ((s: Record<string, unknown>) => ({ ...s, env: e.name === "production" ? undefined : e.name })) as never }))}>
                      {e.name}
                    </Item>
                  ))}
                </Group>
              )}

              {admin && (
                <Group heading="Admin">
                  {adminNav.map((n) => (
                    <Item key={n.key} value={`admin ${n.label}`} icon={n.icon} hint={<span className="flex gap-1"><Kbd>G</Kbd><Kbd>{n.key2.toUpperCase()}</Kbd></span>} onSelect={run(() => navigate({ to: n.to }))}>
                      {n.label}
                    </Item>
                  ))}
                </Group>
              )}

              {registered.map((g) => (
                <Group key={g.key} heading={g.heading}>
                  {g.commands.map((c) => (
                    <Item key={c.id} value={`${g.heading} ${c.label}`} keywords={c.keywords} icon={c.icon} hint={c.hint} onSelect={run(c.run)}>
                      {c.label}
                    </Item>
                  ))}
                </Group>
              ))}

              {workspace && (
              <Group heading="Projects">
                {projects.data?.data.map((p) => (
                  <Item key={p.id} value={`project ${p.name} ${p.slug}`} icon={<FolderKanban />} hint={<GradeBadge grade={p.safety?.grade} size="sm" />} onSelect={run(() => navigate({ to: "/$workspace/$project", params: { workspace, project: p.slug } }))}>
                    {p.name}
                  </Item>
                ))}
                <Item value="new project" icon={<Plus />} onSelect={run(() => navigate({ to: "/$workspace/new", params: { workspace } }))}>
                  New project
                </Item>
              </Group>
              )}

              <Group heading="General">
                <Item value="ask guv ai assistant" icon={<Sparkles />} onSelect={run(() => askGuv({ question: "" }))}>
                  Ask Guv
                </Item>
                <Item value="toggle theme dark light" icon={theme.resolved === "dark" ? <Sun /> : <Moon />} onSelect={run(theme.toggle)}>
                  Switch to {theme.resolved === "dark" ? "light" : "dark"} theme
                </Item>
                <Item value="high contrast accessibility" icon={<Contrast />} onSelect={run(() => theme.setContrast(theme.contrast === "high" ? "normal" : "high"))}>
                  {theme.contrast === "high" ? "Turn off" : "Turn on"} high contrast
                </Item>
                {homeWorkspace && (
                  <Item value="workspace settings members billing" icon={<Settings2 />} onSelect={run(() => (onAdminHost ? window.location.assign(appUrl(`/${homeWorkspace}/settings`)) : navigate({ to: "/$workspace/settings", params: { workspace: homeWorkspace } })))}>
                    Workspace settings
                  </Item>
                )}
                {isAdmin && !admin && (
                  <Item value="platform admin console servers tenants accounts" icon={<ShieldHalf />} onSelect={run(() => (adminElsewhere ? window.location.assign(adminUrl("/admin")) : navigate({ to: "/admin" })))}>
                    Platform admin
                  </Item>
                )}
                {admin && homeWorkspace && (
                  <Item value="back to my workspace projects" icon={<FolderKanban />} onSelect={run(() => (onAdminHost ? window.location.assign(appUrl(`/${homeWorkspace}`)) : navigate({ to: "/$workspace", params: { workspace: homeWorkspace } })))}>
                    Back to your projects
                  </Item>
                )}
                <Item value="keyboard shortcuts" icon={<Keyboard />} hint={<Kbd>?</Kbd>} onSelect={run(openShortcuts)}>
                  Keyboard shortcuts
                </Item>
                <Item value="documentation docs help" icon={<BookOpen />} onSelect={run(() => window.open(docsUrl("/docs"), "_blank"))}>
                  Documentation
                </Item>
                {!onAdminHost && (
                  <Item value="design system style guide" icon={<Palette />} onSelect={run(() => navigate({ to: "/design" }))}>
                    Design system
                  </Item>
                )}
              </Group>
            </Command.List>
          </Command>
        </D.Content>
      </D.Portal>
    </D.Root>
  );
}
